// @ts-check
/**
 * Kurikulum podľa oficiálnej metodiky ATF Online (slovenská QWERTZ).
 *
 * Postupnosť lekcií a poradie znakov sú prevzaté z manuálu predlohy
 * (`atfonlinehelp/vyukaqwertz.htm`) a z ponuky lekcií v aplikácii —
 * tam je uvedených 66 lekcií. Poradie je metodika, nie text: drily, slová
 * a vety v tomto súbore sú VLASTNÉ, generované deterministicky.
 *
 * Základná poloha prstov podľa predlohy (overené v `zakladna_poloha.htm`):
 *   ĽAVÁ:  A malíček, S prstenník, D prostredník, F ukazovák
 *   PRAVÁ: ô malíček, L prstenník, K prostredník, J ukazovák
 * Preto sa začína {d f j k} (prostredníky a ukazováky) a ô je domovská klávesa
 * pravého malíčka — na anglickej klávesnici je na tom mieste bodkočiarka.
 *
 * Poznámka k neoznačeným lekciám: predloha má pri lekciách 45–49, 54, 56 a 63–66
 * prázdne označenie aj vo vlastnej ponuke. Ich obsah je tu preto interpretácia
 * (označená `interpreted: true`) a v UI je to viditeľné.
 */
import { generateDrill, generateSentences, validateItems } from '../engine/drillGenerator.js';
import { targetSpeedForLesson, periodForLesson, DEFAULT_TARGET_ERROR_RATE } from '../engine/grading.js';
import { REFERENCE_MODES, defaultModeFor, hasDrills, slugsFor } from './reference-modes.js';
import { PRACTICE_TEXTS, sentencesForAlphabet, textLinesForAlphabet } from './texts.js';

/**
 * Vyberie `count` položiek deterministicky (rovnaký vstup = rovnaký výber).
 * @param {string[]} items
 * @param {number} count
 * @param {number} seed
 */
function takeDeterministic(items, count, seed) {
  const result = [];
  for (let index = 0; index < count; index++) {
    result.push(items[(seed + index * 7) % items.length]);
  }
  return result;
}

/** Spoločný slovník — generátor z neho vyberie len slová zložené z naučených znakov. */
export const WORDS = [
  'ad', 'as', 'ak', 'aj', 'až', 'dla', 'dlaň', 'dva', 'kad', 'kas',
  'sad', 'sal', 'klad', 'fajka', 'lada', 'lad', 'jas', 'kefa', 'kôl', 'kôš',
  'les', 'list', 'mama', 'mesto', 'most', 'more', 'nos', 'osa', 'okno', 'otec',
  'pes', 'pole', 'rak', 'rok', 'ruka', 'sila', 'sen', 'soľ', 'stôl', 'syr',
  'šat', 'ten', 'tiež', 'ucho', 'ulica', 'voda', 'vlna', 'vlak', 'žiak', 'žena',
  'zem', 'čas', 'čaj', 'ďak', 'hra', 'hlas', 'hmat', 'chlieb', 'chyba', 'ísť',
  'jazyk', 'jeden', 'kniha', 'kôň', 'kraj', 'krok', 'lampa', 'láska', 'lúka',
  'mäso', 'mier', 'myš', 'nádej', 'noc', 'obraz', 'pero', 'piesok', 'prst',
  'rad', 'rieka', 'sneh', 'srdce', 'svet', 'tvár', 'učiteľ', 'úder', 'veta',
  'znak', 'život', 'žiara', 'kôra', 'váha', 'dnes', 'včera', 'zajtra', 'ráno',
  'večer', 'škola', 'domov', 'cesta', 'strom', 'okuliare', 'záhrada', 'výlet',
  'oblak', 'mesiac', 'slnko', 'hviezda', 'počasie', 'chvíľa', 'súhlas', 'úvaha',
];

/**
 * @typedef {Object} LessonSeed
 * @property {number} n                 oficiálne číslo lekcie 1–66
 * @property {string[]} [keys]          novo preberané znaky (podľa predlohy)
 * @property {boolean} [repeat]         opakovacia lekcia („Opak.“)
 * @property {boolean} [shift]          lekcia o Shiftu a veľkých písmenách
 * @property {boolean} [interpreted]    obsah je interpretácia (predloha lekciu neoznačuje)
 * @property {boolean} [practice]       precvičovacia lekcia (podľa predlohy bez drilov)
 * @property {string} [title]           vlastný titulok tam, kde predloha žiadny neuvádza
 * @property {string} [note]            poznámka k metodike
 */

/**
 * Oficiálna postupnosť 66 lekcií pre slovenskú QWERTZ.
 * `keys` sú prevzaté z ponuky lekcií predlohy (`Lekcia 1 {dfjk}` … `Lekcia 62 {2}`).
 * @type {LessonSeed[]}
 */
export const OFFICIAL_SEQUENCE = [
  { n: 1, keys: ['d', 'f', 'j', 'k'] },
  { n: 2, keys: ['a', 'ô'] },
  { n: 3, keys: ['l', 's'] },
  { n: 4, repeat: true },
  { n: 5, keys: ['e', ','] },
  { n: 6, keys: ['w', 'x', 'o'] },
  { n: 7, keys: ['t', 'n'] },
  { n: 8, repeat: true },
  { n: 9, keys: ['c', 'i'] },
  { n: 10, keys: ['q', 'y', 'p'] },
  { n: 11, keys: ['r', 'm'] },
  { n: 12, repeat: true },
  { n: 13, keys: ['u'] },
  { n: 14, keys: ['v'] },
  { n: 15, keys: ['z'] },
  { n: 16, keys: ['b'] },
  { n: 17, repeat: true },
  { n: 18, keys: ['-'] },
  { n: 19, keys: ['h'] },
  { n: 20, keys: ['g'] },
  { n: 21, keys: ['ú'] },
  { n: 22, repeat: true },
  { n: 23, shift: true, title: 'Veľké písmená (Shift)' },
  { n: 24, keys: ['.'] },
  { n: 25, shift: true, title: 'Veľké písmená a interpunkcia (Shift)' },
  { n: 26, repeat: true },
  { n: 27, repeat: true },
  { n: 28, repeat: true },
  { n: 29, repeat: true },
  { n: 30, repeat: true },
  { n: 31, keys: ['ý'] },
  { n: 32, keys: ['ť'] },
  { n: 33, keys: ['á'] },
  { n: 34, keys: ['č'] },
  { n: 35, keys: ['í'] },
  { n: 36, repeat: true },
  { n: 37, keys: ['š'] },
  { n: 38, keys: ['é'] },
  { n: 39, keys: ['ľ'] },
  { n: 40, keys: ['ž'] },
  { n: 41, keys: ['ä', 'ň'] },
  { n: 42, repeat: true },
  { n: 43, keys: ['ď'], title: 'Mäkčeň ˇ — skladané znaky', note: 'Mäkčeň a písmeno sa píše v dvoch krokoch; ď sa preto počíta ako 3 údery.' },
  { n: 44, keys: ['ó', 'ŕ', 'ĺ'], title: 'Dĺžeň ´ — skladané znaky', note: 'ó, ŕ a ĺ sa skladajú dĺžňom; počítajú sa ako 2 údery.' },
  // 45–49: predloha ich neoznačuje a nemajú režim Hmaty (drily) — podľa zistených
  // režimov ide o prácu so slovami a textom, nie o nové znaky. Tieto titulky sú naše.
  { n: 45, interpreted: true, practice: true, title: 'Opakovanie — slová a text' },
  { n: 46, interpreted: true, practice: true, title: 'Slová z textov' },
  { n: 47, interpreted: true, repeat: true, practice: true, title: 'Slová a vety' },
  { n: 48, interpreted: true, practice: true, title: 'Slová a vety' },
  { n: 49, interpreted: true, practice: true, title: 'Slová a text' },
  { n: 50, keys: ['?', '!'] },
  { n: 51, keys: [':', ';'] },
  { n: 52, keys: ['"'] },
  { n: 53, keys: ['(', ')', '/'] },
  { n: 54, interpreted: true, practice: true, title: 'Text na precvičenie' },
  { n: 55, keys: ['_'] },
  { n: 56, interpreted: true, practice: true, title: 'Text na precvičenie' },
  { n: 57, keys: ['5', '8', '§'] },
  { n: 58, keys: ['4', '9', '%'] },
  { n: 59, keys: ['3', '0'] },
  { n: 60, keys: ['1', '+', '='] },
  { n: 61, keys: ['6', '7'] },
  { n: 62, keys: ['2'] },
  { n: 63, interpreted: true, repeat: true, practice: true, title: 'Opakovanie — slová a text' },
  { n: 64, interpreted: true, practice: true, title: 'Slová z textov' },
  { n: 65, interpreted: true, practice: true, title: 'Slová a text' },
  { n: 66, interpreted: true, practice: true, title: 'Záverečný text' },
];

const LETTERS = 'abcdefghijklmnopqrstuvwxyzúäôňďóŕĺýáčšíéľžť';

/**
 * @typedef {Object} Lesson
 * @property {string} id
 * @property {number} number         oficiálne číslo lekcie
 * @property {number} period         obdobie výučby (1–10), podľa ktorého sa známkuje
 * @property {string} title
 * @property {string} blockTitle
 * @property {string} blockNote
 * @property {string[]} newKeys
 * @property {string[]} allowedKeys
 * @property {string[]} items
 * @property {'drill'|'words'|'sentence'} type
 * @property {Record<string, string[]>} exercises  cvičenia po režimoch (hmaty, slova, vety, text)
 * @property {string[]} modes                      režimy lekcie ako naše identifikátory
 * @property {string[]} referenceModes  režimy, ktoré pre lekciu ponúka predloha
 * @property {boolean} hasReferenceDrills  má predloha pre lekciu drily (režim Hmaty)?
 * @property {{cpm: number, accuracy: number}} targets
 * @property {boolean} interpreted
 */

/**
 * Zostaví hotové lekcie: kumuluje `allowedKeys` v oficiálnom poradí,
 * doplní veľké písmená v lekciách o Shifte a vygeneruje drily.
 * @returns {Lesson[]}
 */
export function buildCurriculum() {
  /** @type {Set<string>} */
  const allowed = new Set();
  /** @type {Lesson[]} */
  const lessons = [];

  for (const seed of OFFICIAL_SEQUENCE) {
    const newKeys = [];

    if (seed.shift) {
      // Lekcia o Shifte: pribudnú veľké písmená doteraz naučených písmen.
      // Rozdelia sa medzi obe shiftové lekcie (23 a 25), aby sa v každej cvičení
      // naozaj objavili — a aby to zodpovedalo dvom shiftovým lekciám v predlohe.
      const candidates = [];
      for (const key of allowed) {
        if (!LETTERS.includes(key)) continue;
        const upper = key.toUpperCase();
        if (upper !== key) candidates.push(upper);
      }
      const half = Math.ceil(candidates.length / 2);
      const slice = seed.n <= 23 ? candidates.slice(0, half) : candidates.slice(half);
      for (const upper of slice) {
        if (!allowed.has(upper)) newKeys.push(upper);
      }
      if (seed.n === 25) {
        // V druhej shiftovej lekcii aj znaky, ktoré sa píšu so Shiftom.
        for (const key of [',', '.', '-']) {
          if (!allowed.has(key)) newKeys.push(key);
        }
      }
    } else if (seed.keys) {
      newKeys.push(...seed.keys);
    }

    for (const key of newKeys) allowed.add(key);

    // Po shiftových lekciách (23 a 25) musí mať každé nové malé písmeno aj svoju
    // veľkú podobu — inak by sa napríklad „Ž“ z lekcie 40 nedalo nikdy napísať,
    // pretože Shift sa preberal skôr, než diakritika.
    if (seed.n > 25) {
      for (const key of newKeys) {
        if (!LETTERS.includes(key)) continue;
        const upper = key.toUpperCase();
        if (upper !== key) allowed.add(upper);
      }
    }

    const allowedKeys = [...allowed];

    // Po druhej shiftovej lekcii vieme písať veľké písmená, preto ich drily môžu používať.
    const pattern = seed.n <= 3 ? 'pairs' : seed.keys && seed.keys.some((k) => k.length === 1 && LETTERS.includes(k)) ? 'pairs' : 'random';

    const modes = slugsFor(seed.n);
    const primaryMode = modes[0] ?? 'slova';

    // Drily na nové znaky. Predloha ich má v režime Hmaty; lekcie bez Hmaty
    // (napr. čísla a interpunkcia) drily nemajú, ale nové znaky prebrať musia —
    // preto sa v takom prípade pripravia na začiatok prvého dostupného režimu.
    // Drily sú tam kratšie, aby nezabrali celý režim (stačia na pokrytie znakov).
    const drillLines = modes.includes('hmaty') ? 4 : 2;
    const drillItems = modes.includes('hmaty') || newKeys.length > 0
      ? generateDrill({
          allowedKeys,
          newKeys,
          pattern,
          lines: drillLines,
          groupsPerLine: 6,
          seed: 100 * seed.n + 1,
        })
      : [];

    const wordItems = generateDrill({
      allowedKeys,
      pattern: 'words',
      words: WORDS,
      lines: 5,
      groupsPerLine: 5,
      seed: 9000 + seed.n,
    });

    // Vety: prednosť majú skutočné vety z našich textov, ak sa dajú napísať
    // z naučených znakov; inak sa vygenerujú z naučených slov.
    const realSentences = sentencesForAlphabet(allowedKeys);
    const sentenceItems = realSentences.length >= 4
      ? takeDeterministic(realSentences, 5, 7000 + seed.n)
      : generateSentences({ allowedKeys, words: WORDS, seed: 5000 + seed.n, lines: 5 });

    // Text: riadky našich vlastných textov, ktoré sa dajú napísať z naučených znakov.
    // Preferujeme dlhšie riadky — režim „Text“ má byť súvislé písanie, nie fragmenty.
    const allTextLines = PRACTICE_TEXTS.flatMap((text) => textLinesForAlphabet(text, allowedKeys));
    const longTextLines = allTextLines.filter((line) => line.length >= 30);
    const realTextLines = longTextLines.length >= 6 ? longTextLines : allTextLines;
    const textItems = realTextLines.length >= 6
      ? takeDeterministic(realTextLines, 8, 3000 + seed.n)
      : (realSentences.length > 0 ? takeDeterministic(realSentences, 6, 4000 + seed.n) : sentenceItems);

    /** @type {Record<string, string[]>} */
    const exercises = {};
    if (modes.includes('hmaty')) exercises.hmaty = drillItems;
    if (modes.includes('slova')) exercises.slova = wordItems;
    if (modes.includes('vety')) exercises.vety = sentenceItems;
    if (modes.includes('text')) exercises.text = textItems;

    // Nové znaky musia byť vždy precvičené, aj keď lekcia nemá režim Hmaty.
    if (drillItems.length > 0 && !exercises.hmaty) {
      exercises[primaryMode] = [...drillItems, ...(exercises[primaryMode] ?? [])];
    }

    const items = exercises[defaultModeFor(seed.n)] ?? Object.values(exercises)[0] ?? [];

    const targetSpeed = targetSpeedForLesson(seed.n);
    lessons.push({
      id: `lekcia-${String(seed.n).padStart(2, '0')}`,
      number: seed.n,
      period: periodForLesson(seed.n),
      title: seed.title ?? `Nové znaky: ${newKeys.join(' ')}`,
      blockTitle: `${periodForLesson(seed.n)}. obdobie výučby`,
      blockNote: `Cieľová rýchlosť v tomto období: ${targetSpeed} čistých úderov/min (podľa metodiky predlohy).`,
      newKeys,
      allowedKeys,
      items,
      exercises,
      modes,
      type: modes.includes('hmaty') ? 'drill' : 'words',
      referenceModes: REFERENCE_MODES[seed.n] ?? [],
      hasReferenceDrills: hasDrills(seed.n),
      targets: { cpm: targetSpeed, accuracy: 1 - DEFAULT_TARGET_ERROR_RATE / 100 },
      interpreted: !!seed.interpreted,
    });
  }

  return lessons;
}

export const LESSONS = buildCurriculum();

/** @param {string} id */
export function getLesson(id) {
  return LESSONS.find((lesson) => lesson.id === id) ?? null;
}

/**
 * Lekcia, ktorá nasleduje po danej (alebo prvá, ak id nie je známe).
 * @param {string} id
 */
export function nextLesson(id) {
  const index = LESSONS.findIndex((lesson) => lesson.id === id);
  if (index === -1) return LESSONS[0];
  return LESSONS[index + 1] ?? null;
}

/** Zoskupenie lekcií do desiatich období výučby (podľa metodiky predlohy). */
export function lessonsByBlock() {
  /** @type {Map<number, Lesson[]>} */
  const map = new Map();
  for (const lesson of LESSONS) {
    if (!map.has(lesson.period)) map.set(lesson.period, []);
    map.get(lesson.period)?.push(lesson);
  }
  return [...map.entries()].map(([block, lessons]) => ({
    block,
    title: lessons[0].blockTitle,
    note: lessons[0].blockNote,
    lessons,
  }));
}

/**
 * Kontrola kurikula — ak obsah porušuje pravidlá, zlyhá build (aj test).
 * Overuje aj to, že poradie znakov sedí s oficiálnou postupnosťou.
 */
export function validateCurriculum() {
  /** @type {string[]} */
  const problems = [];
  const seen = new Set();

  for (const lesson of LESSONS) {
    if (seen.has(lesson.id)) problems.push(`duplicitné id lekcie: ${lesson.id}`);
    seen.add(lesson.id);

    const missing = lesson.newKeys.filter((key) => !lesson.allowedKeys.includes(key));
    if (missing.length > 0) problems.push(`${lesson.id}: newKeys nie sú v allowedKeys (${missing.join(', ')})`);

    problems.push(...validateItems(lesson.items, lesson.allowedKeys).map((p) => `${lesson.id}: ${p}`));

    // Každý režim lekcie musí byť neprázdny a nesmie obsahovať nenaučený znak.
    if (lesson.modes.length === 0) problems.push(`${lesson.id}: lekcia nemá žiadny režim`);
    for (const [mode, modeItems] of Object.entries(lesson.exercises)) {
      if (modeItems.length === 0) problems.push(`${lesson.id}: režim ${mode} je prázdny`);
      problems.push(...validateItems(modeItems, lesson.allowedKeys).map((p) => `${lesson.id} [${mode}]: ${p}`));
    }
    // Režimy musia sedieť s tým, čo pre lekciu ponúka predloha.
    if (lesson.modes.join('|') !== slugsFor(lesson.number).join('|')) {
      problems.push(`${lesson.id}: režimy nezodpovedajú predlohe`);
    }

    if (!lesson.targets || typeof lesson.targets.cpm !== 'number') problems.push(`${lesson.id}: chýbajú ciele`);
    if (lesson.items.length === 0) problems.push(`${lesson.id}: žiadne cvičenie`);
    if (lesson.number < 1 || lesson.number > 66) problems.push(`${lesson.id}: číslo lekcie mimo 1–66`);
  }

  if (LESSONS.length !== OFFICIAL_SEQUENCE.length) {
    problems.push(`počet lekcií ${LESSONS.length} nezodpovedá oficiálnej postupnosti ${OFFICIAL_SEQUENCE.length}`);
  }

  return problems;
}

/** Zoznam lekcií, ktoré predloha neoznačuje a ktorých obsah je interpretácia. */
export function interpretedLessons() {
  return LESSONS.filter((lesson) => lesson.interpreted);
}
